import React, {ReactElement, useContext, useEffect, useState} from "react";
import {ProviderContext} from "../providerContext";
import ReactECharts from "echarts-for-react";
import DateRangePicker, {
    DateRange,
    DateRangeItemKey,
    getDefaultDateRange,
} from "../../components/datePicker/dateRangePicker";
import {formatDate} from "../../util/util";
import moment from "moment";
import ToolTips, {ToolTipPosition} from "../../components/toolTips/toolTips";
import {useHistory} from "react-router-dom";
import {RoutePath} from "../../router/routePath";
import {AutomatedCampaignType} from "../campaign/dataForActiveCampaign";
import {$iconCalendar} from "../../assets/icons";
import {
    getAppointmentNoShowCount,
    getAppointmentReminderCount,
    getAppointmentReminderDashboardData,
    getDashboardMenuData,
} from "./dashboardService";
import {PromptContext} from "../promptContext";
import Spinner from "../../components/spinner/spinner";
import {
    ChartsData,
    MenuSource,
    prepareEChartsOptions,
} from "./dataForDashboard";
import {APIError} from "../../util/http/HTTP";

type GetDashboardDataFunc = (dateRange: DateRange, success: (data: any) => void, fail: (error: APIError) => void) => void

export default function Dashboard() {
    const [showCalendarPicker, setShowCalendarPicker] = useState<boolean>(false)
    const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange())
    const [showSpinnerCount, setShowSpinnerCount] = useState<number>(0)
    const [menuSource, setMenuSource] = useState<MenuSource>()
    const [aptReminderChartsData, setAptReminderChartsData] = useState<Array<ChartsData>>([])

    const history = useHistory()
    const provider = useContext(ProviderContext)
    const {showError} = useContext(PromptContext)

    useEffect(() => {
        executeFunction(getDashboardMenuData, dateRange, (data: any) => {
            setMenuSource(data)
        })
        executeFunction(getAppointmentReminderDashboardData, dateRange, (data: any) => {
            updateAptReminderChartsData(data)
        })
        executeFunction(getAppointmentNoShowCount, dateRange, (data: any) => {
            updateAptReminderChartsData(data)
        })
        executeFunction(getAppointmentReminderCount, dateRange, (data: any) => {
            updateAptReminderChartsData(data)
        })
    }, [dateRange])

    const executeFunction = (func: GetDashboardDataFunc, dateRange: DateRange, success: (data: any) => void) => {
        const onSuccess = (data: any) => {
            dismissSpinner()
            success && success(data)
        }
        const onError = (error: APIError) => {
            dismissSpinner()
            showError(error)
        }
        func(dateRange, onSuccess, onError)
        showSpinner()
    }

    const showSpinner = () => {
        setShowSpinnerCount((pre) => pre + 1)
    }

    const dismissSpinner = () => {
        setShowSpinnerCount((pre) => pre - 1)
    }

    const updateAptReminderChartsData = (newData: Array<ChartsData>) => {
        if (newData && newData.length) {
            setAptReminderChartsData((pre) => {
                const dt = newData.concat(pre.filter((item) => newData.find((_item) => _item.name === item.name) === undefined))
                return dt.sort((a, b) => a.sortKey - b.sortKey)
            })
        }
    }

    const onShowDatePicker = () => {
        setShowCalendarPicker(true)
    }

    const $activeCampaignIcon = (
        <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path opacity="0.4" d="M13.191 0H4.81C1.77 0 0 1.78 0 4.83V15.16C0 18.26 1.77 20 4.81 20H13.191C16.28 20 18 18.26 18 15.16V4.83C18 1.78 16.28 0 13.191 0Z" fill="#2F80ED"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M5.07996 4.6499V4.6599C4.64896 4.6599 4.29996 5.0099 4.29996 5.4399C4.29996 5.8699 4.64896 6.2199 5.07996 6.2199H8.06896C8.49996 6.2199 8.84996 5.8699 8.84996 5.4289C8.84996 4.9999 8.49996 4.6499 8.06896 4.6499H5.07996ZM12.92 10.7399H5.07996C4.64896 10.7399 4.29996 10.3899 4.29996 9.9599C4.29996 9.5299 4.64896 9.1789 5.07996 9.1789H12.92C13.35 9.1789 13.7 9.5299 13.7 9.9599C13.7 10.3899 13.35 10.7399 12.92 10.7399ZM12.92 15.3099H5.07996C4.77996 15.3499 4.48996 15.1999 4.32996 14.9499C4.16996 14.6899 4.16996 14.3599 4.32996 14.1099C4.48996 13.8499 4.77996 13.7099 5.07996 13.7399H12.92C13.319 13.7799 13.62 14.1199 13.62 14.5299C13.62 14.9289 13.319 15.2699 12.92 15.3099Z" fill="#2F80ED"/>
        </svg>
    )

    const $smsSentIcon = (
        <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path opacity="0.4" d="M13.591 0H5.21002C2.17002 0 0.400024 1.78 0.400024 4.83V15.16C0.400024 18.26 2.17002 20 5.21002 20H13.591C16.68 20 18.4 18.26 18.4 15.16V4.83C18.4 1.78 16.68 0 13.591 0" fill="#F2994A"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M5.47999 4.6499V4.6599C5.04899 4.6599 4.69999 5.0099 4.69999 5.4399C4.69999 5.8699 5.04899 6.2199 5.47999 6.2199H8.46899C8.89999 6.2199 9.24999 5.8699 9.24999 5.4289C9.24999 4.9999 8.89999 4.6499 8.46899 4.6499H5.47999ZM13.32 10.7399H5.47999C5.04899 10.7399 4.69999 10.3899 4.69999 9.9599C4.69999 9.5299 5.04899 9.1789 5.47999 9.1789H13.32C13.75 9.1789 14.1 9.5299 14.1 9.9599C14.1 10.3899 13.75 10.7399 13.32 10.7399ZM13.32 15.3099H5.47999C5.17999 15.3499 4.88999 15.1999 4.72999 14.9499C4.56999 14.6899 4.56999 14.3599 4.72999 14.1099C4.88999 13.8499 5.17999 13.7099 5.47999 13.7399H13.32C13.719 13.7799 14.02 14.1199 14.02 14.5299C14.02 14.9289 13.719 15.2699 13.32 15.3099Z" fill="#F2994A"/>
        </svg>
    )

    const $patientEngagedIcon = (
        <svg width="21" height="18" viewBox="0 0 21 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.14938 11.8577C4.18558 11.8577 0.800049 12.47 0.800049 14.9173C0.800049 17.3665 4.16405 17.9999 8.14938 17.9999C12.1132 17.9999 15.4987 17.3876 15.4987 14.9403C15.4987 12.4911 12.1347 11.8577 8.14938 11.8577Z" fill="#219653"/>
            <path opacity="0.4" d="M8.1494 9.52482C10.849 9.52482 13.0124 7.40617 13.0124 4.76241C13.0124 2.11865 10.849 0 8.1494 0C5.45077 0 3.28638 2.11865 3.28638 4.76241C3.28638 7.40617 5.45077 9.52482 8.1494 9.52482Z" fill="#219653"/>
            <path opacity="0.4" d="M14.9736 4.84873C14.9736 6.19505 14.5607 7.45129 13.8366 8.4948C13.7612 8.60212 13.8278 8.74682 13.9589 8.76981C14.1409 8.79952 14.3278 8.81773 14.5186 8.82156C16.4168 8.87043 18.1204 7.6736 18.591 5.87116C19.2887 3.19674 17.2417 0.79541 14.634 0.79541C14.3513 0.79541 14.0802 0.824157 13.816 0.87686C13.7798 0.884526 13.7407 0.901774 13.7211 0.932437C13.6957 0.971725 13.7143 1.02251 13.7397 1.05605C14.5235 2.13214 14.9736 3.44205 14.9736 4.84873Z" fill="#219653"/>
            <path d="M20.5792 12.1693C20.2319 11.4439 19.3933 10.9465 18.1174 10.7022C17.5156 10.5585 15.8855 10.3544 14.3698 10.3831C14.3473 10.386 14.3346 10.4013 14.3327 10.4109C14.3297 10.4262 14.3366 10.4492 14.3659 10.4655C15.0665 10.8047 17.774 12.2804 17.4334 15.3927C17.4188 15.5288 17.5293 15.6438 17.6673 15.6246C18.3336 15.5317 20.0479 15.1704 20.5792 14.0474C20.8738 13.4533 20.8738 12.7634 20.5792 12.1693Z" fill="#219653"/>
        </svg>
    )

    const $newAppointmentIcon = (
        <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path opacity="0.4" d="M15.4089 7.021C14.9574 7.021 14.3593 7.011 13.6147 7.011C11.7988 7.011 10.3056 5.508 10.3056 3.675V0.459C10.3056 0.206 10.1027 0 9.85314 0H4.56372C2.09526 0 0.100098 2.026 0.100098 4.509V15.284C0.100098 17.889 2.19032 20 4.76968 20H12.6454C15.1059 20 17.1001 17.987 17.1001 15.502V7.471C17.1001 7.217 16.8981 7.012 16.6466 7.013C16.2248 7.016 15.7168 7.021 15.4089 7.021Z" fill="#F2C94C"/>
            <path opacity="0.4" d="M12.6843 0.56737C12.3853 0.25637 11.8633 0.47037 11.8633 0.90137V3.53837C11.8633 4.64437 12.7743 5.55437 13.8793 5.55437C14.5773 5.56237 15.5453 5.56437 16.3673 5.56237C16.7883 5.56137 17.0023 5.05837 16.7103 4.75437C15.6553 3.65737 13.7663 1.69137 12.6843 0.56737Z" fill="#F2C94C"/>
            <path d="M10.9673 10.2364H9.2393V8.5094C9.2393 8.0984 8.9063 7.7644 8.4953 7.7644C8.0843 7.7644 7.7503 8.0984 7.7503 8.5094V10.2364H6.0233C5.6123 10.2364 5.2793 10.5704 5.2793 10.9814C5.2793 11.3924 5.6123 11.7264 6.0233 11.7264H7.7503V13.4524C7.7503 13.8634 8.0843 14.1974 8.4953 14.1974C8.9063 14.1974 9.2393 13.8634 9.2393 13.4524V11.7264H10.9673C11.3783 11.7264 11.7123 11.3924 11.7123 10.9814C11.7123 10.5704 11.3783 10.2364 10.9673 10.2364Z" fill="#F2C94C"/>
        </svg>
    )

    const $timeSavedIcon = (
        <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path opacity="0.4" d="M20.1997 10C20.1997 15.524 15.7227 20 10.1997 20C4.67671 20 0.199707 15.524 0.199707 10C0.199707 4.478 4.67671 0 10.1997 0C15.7227 0 20.1997 4.478 20.1997 10Z" fill="#BB6BD9"/>
            <path d="M13.7734 13.8145C13.6424 13.8145 13.5104 13.7805 13.3894 13.7095L9.46339 11.3675C9.23739 11.2315 9.09839 10.9865 9.09839 10.7225V5.67554C9.09839 5.26154 9.43439 4.92554 9.84839 4.92554C10.2624 4.92554 10.5984 5.26154 10.5984 5.67554V10.2965L14.1584 12.4195C14.5134 12.6325 14.6304 13.0925 14.4184 13.4485C14.2774 13.6835 14.0284 13.8145 13.7734 13.8145Z" fill="#BB6BD9"/>
        </svg>
    )

    const $menuItem = (icon: ReactElement, iconBgColor: string, title: string, value: string, onClick?: Function) => {
        return (
            <div className={`flex flex-row items-center bg-white rounded-xl shadow-md p-4 ${onClick ? "cursor-pointer" : ""}`} onClick={() => {
                onClick && onClick()
            }}>
                <div className={"min-w-max rounded-xl"} style={{backgroundColor: iconBgColor}}>
                    <div className={"w-14 h-14 flex justify-center items-center"}>
                        {icon}
                    </div>
                </div>
                <div className={"flex flex-col justify-center ml-4 overflow-hidden"}>
                    <p className={"text-sm text-gray-2 truncate"}>{title}</p>
                    <p className={"text-lg text-gray-1 font-semibold truncate"}>{value}</p>
                </div>
            </div>
        )
    }

    const $calendarIcon = (
        <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1.57715 7.83688H16.4304" stroke="#828282" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12.7017 11.0915H12.7094" stroke="#828282" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9.00397 11.0915H9.01169" stroke="#828282" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M5.29816 11.0915H5.30588" stroke="#828282" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12.7017 14.3303H12.7094" stroke="#828282" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9.00397 14.3303H9.01169" stroke="#828282" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M5.29816 14.3303H5.30588" stroke="#828282" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12.3699 1.66675V4.40906" stroke="#828282" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M5.63795 1.66675V4.40906" stroke="#828282" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M12.5319 2.98267H5.4758C3.02856 2.98267 1.5 4.34594 1.5 6.85185V14.3932C1.5 16.9385 3.02856 18.3333 5.4758 18.3333H12.5242C14.9791 18.3333 16.5 16.9622 16.5 14.4563V6.85185C16.5077 4.34594 14.9868 2.98267 12.5319 2.98267Z" stroke="#828282" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    )

    const $arrowIcon = (
        <svg width="12" height="6" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.5 2.25L6.01841 3.99079L2.53683 2.25" stroke="#828282" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    )

    let dateRangeTitle = dateRange.title
    const fromDate = dateRange.from
    const toDate = dateRange.to
    if (dateRange.id === DateRangeItemKey.custom) {
        if (moment(fromDate).isSame(moment(toDate), "day")) {
            dateRangeTitle = formatDate(fromDate)
        } else if (fromDate.getFullYear() === toDate.getFullYear()) {
            dateRangeTitle = `${formatDate(fromDate).split(",")[0]} - ${formatDate(toDate)}`
        } else {
            dateRangeTitle = `${formatDate(fromDate)} - ${formatDate(toDate)}`
        }
    }
    const $datePicker = (
        <div className={"flex flex-row space-between items-center p-3 rounded-lg bg-white cursor-pointer"} onClick={onShowDatePicker}>
            <div className={"w-full flex flex-row items-center"}>
                {$calendarIcon}
                <p className={"text-sm mx-3"}>{dateRangeTitle}</p>
            </div>
            {$arrowIcon}
        </div>
    )

    const $header = (
        <div className={"flex flex-row items-center justify-between mb-2"}>
            <p className={"text-gary-1 text-2xl font-bold"}>{`Welcome, ${provider.employeeFirstName}`}</p>
            {$datePicker}
        </div>
    )

    const activeCampaignCount = menuSource?.activeCampaign?.count || 0
    const smsCount = menuSource?.sentSMS?.count || 0
    // const smsCost = menuSource?.sentSMS?.totalCost || 0
    const engagedPatientsCount = menuSource?.engagedPatient?.count || 0
    const savedTime = menuSource?.savedTime?.count || 0
    const newAppointmentCount = menuSource?.newAppointment?.count || 0

    const $menu = (
        <div className={"flex flex-row items-center grid grid-cols-5 gap-6 py-6"}>
            {$menuItem($activeCampaignIcon, "#2F80ED33", "Active Campaigns", activeCampaignCount > 0 ? `${activeCampaignCount} ${activeCampaignCount > 1 ? "Campaigns" : "Campaign"}` : "--", () => {
                history.replace(RoutePath.campaign)
            })}
            {/*<ToolTips disable={smsCost <= 0} position={ToolTipPosition.top} tips={`approx. cost: $${smsCost}`}>*/}
            {/*    {$menuItem($smsSentIcon, "#F2994A33", "SMS Sent", smsCount > 0 ? `${smsCount} ${smsCount > 1 ? "Messages" : "Message"}` : "--")}*/}
            {/*</ToolTips>*/}
            {$menuItem($smsSentIcon, "#F2994A33", "SMS Sent", smsCount > 0 ? `${smsCount} ${smsCount > 1 ? "Messages" : "Message"}` : "--")}
            {$menuItem($patientEngagedIcon, "#21965333", "Patients Engaged", engagedPatientsCount > 0 ? `${engagedPatientsCount} ${engagedPatientsCount > 1 ? "Patients" : "Patient"}` : "--")}
            <ToolTips disable={savedTime <= 0} position={ToolTipPosition.top} tips={"Estimated based on 2 min/call."}>
                {$menuItem($timeSavedIcon, "#BB6BD933", "Time Saved", savedTime > 0 ? `${savedTime} ${savedTime > 1 ? "Minutes" : "Minute"}` : "--")}
            </ToolTips>
            {$menuItem($newAppointmentIcon, "#F2C94C33", "New Appointments", `${newAppointmentCount > 0 ? newAppointmentCount : "--"}`)}
        </div>
    )

    const $dashboardTag = (tagColor: string, title: string) => {
        return (
            <div key={title} className={"flex flex-row items-center px-2 py-1 space-x-2 rounded-md border border-gray-6"}>
                <div className={"w-2.5 h-2.5 rounded"} style={{backgroundColor: tagColor}}/>
                <p className={"text-sm text-gray-2"}>{title}</p>
            </div>
        )
    }

    const aptReminderOption = prepareEChartsOptions(aptReminderChartsData)
    const $appointmentReminderDashboard = aptReminderChartsData?.length ? (
        <div className={"flex flex-col p-4 space-y-4 rounded-xl shadow-lg bg-white"}>
            <p className={"text-lg text-gray-1 font-semibold"}>Appointment Reminder</p>
            <div className={"flex flex-row items-center space-x-2"}>
                {aptReminderChartsData.map((dt) => $dashboardTag(dt.lineColor, dt.name))}
            </div>
            <ReactECharts option={aptReminderOption} />
        </div>
    ) : null

    const newAppointmentReminderCampaign = () => {
        history.push({
            pathname: RoutePath.newCampaign,
            search: `?type=${AutomatedCampaignType.appointment}`,
            state: {
                routePathHidden: true,
            },
        })
    }

    const $emptyAppointmentReminder = (
        <div className={"flex flex-col p-4 space-y-4 rounded-xl shadow-lg bg-white"}>
            <p className={"text-lg text-gray-1 font-semibold"}>Appointment Reminder</p>
            <p className={"text-sm text-gray-3"}>No data available.</p>
            <p className={"text-sm text-gray-2"}>Try out our automated campaign templates to engage with patients effectively.</p>
            <div className={"flex flex-1 flex-row items-center space-x-10 py-4 px-10 border border-gray-200 shadow-md rounded cursor-pointer hover:border-primary"} onClick={newAppointmentReminderCampaign}>
                <div className={"flex flex-none"}>
                    {$iconCalendar}
                </div>
                <div className={"flex flex-1 flex-col space-y-2"}>
                    <p className={"text-sm font-semibold"}>Appointment reminder</p>
                    <p className={"text-xs w-full"}>Remind your patients before the appointment date.</p>
                </div>
            </div>
        </div>
    )

    const textToBookOptions = {
        grid: { top: 8, right: 8, bottom: 24, left: 36 },
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "cross",
                label: {
                    backgroundColor: "#6a7985",
                },
            },
        },
        xAxis: {
            type: "category",
            data: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
        },
        yAxis: {
            type: "value",
        },
        series: [
            {
                name: "Appointment Scheduled",
                type: "line",
                smooth: true,
                stack: "Total",
                lineStyle: {
                    color: "#F2994A",
                },
                emphasis: {
                    focus: "series",
                },
                data: [120, 132, 101, 134, 90, 230, 210],
            },
            {
                name: "Patient Opens",
                type: "line",
                smooth: true,
                stack: "Total",
                lineStyle: {
                    color: "#BB6BD9",
                },
                emphasis: {
                    focus: "series",
                },
                data: [220, 182, 191, 234, 290, 330, 310],
            },
        ],
    };
    const $textToBookDashboard = (
        <div className={"flex flex-col p-4 space-y-4 rounded-xl shadow-lg bg-white"}>
            <p className={"text-lg text-gray-1 font-semibold"}>Text-to-book</p>
            <div className={"flex flex-row items-center space-x-2"}>
                {$dashboardTag("#F2994A", "Appointment Scheduled")}
                {$dashboardTag("#BB6BD9", "Patient Opens")}
            </div>
            <ReactECharts option={textToBookOptions} />
        </div>
    )

    const $emptyTextToBook = (
        <div className={"flex flex-col p-4 space-y-4 rounded-xl shadow-lg bg-white"}>
            <p className={"text-lg text-gray-1 font-semibold"}>Text-to-book</p>
            <p className={"text-sm text-gray-3"}>No data available.</p>
            <p className={"text-sm text-gray-2"}>
                Try our text-to-book feature by including a link of your public website in your campaigns. Patients can book appointments online through your public website directly. If you need help setting up your public website, please contact us at
                <a href="mailto:digitalhealth@mdland.com" className={"ml-1 text-primary underline"} >digitalhealth@mdland.com</a>.
            </p>
        </div>
    )

    const hasTextToBook = true
    const $content = (
        <div className={"w-full space-y-6"}>
            {aptReminderChartsData?.length ? $appointmentReminderDashboard : $emptyAppointmentReminder}
            {!hasTextToBook ? $textToBookDashboard : $emptyTextToBook}
        </div>
    )

    const $dateRangePicker = (
        <DateRangePicker
            isOpen={showCalendarPicker}
            onCancel={() => {
                setShowCalendarPicker(false)
            }}
            onApply={(dr) => {
                setShowCalendarPicker(false)
                if (dr.id !== dateRange.id) {
                    setDateRange(dr)
                    setAptReminderChartsData([])
                } else if (dr.id === DateRangeItemKey.custom ) {
                    const sameRange = moment(dateRange.from).isSame(moment(dr.from), "day") && moment(dateRange.to).isSame(moment(dr.to), "day")
                    if (!sameRange) {
                        setDateRange(dr)
                        setAptReminderChartsData([])
                    }
                }
            }}
        />
    )

    const $spinner = <Spinner isOpen={showSpinnerCount > 0}/>

    return (
        <div className={"w-full px-10 p-6"}>
            {$header}
            {$menu}
            {$content}
            {$dateRangePicker}
            {$spinner}
        </div>
    )
}
